import { defineStore } from 'pinia'
import { ref, computed, watch, nextTick } from 'vue'
import { useTimeoutPoll } from '@vueuse/core'
import { useApi } from '@/composables/useApi'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'
import type { Company, User, UserSyncStatus } from '@trama/shared'

/**
 * Centralized Pinia store for the Settings views (Phase 1).
 *
 * Owns:
 *  - The current user's profile (`profile`) and the company's profile (`company`).
 *  - The `syncStatus` flag for the Logto mirror sync, and the 30s polling
 *    loop that re-fetches `GET /users/me` while sync is in flight.
 *  - The state machine for the "delete my account" flow — the dialog calls
 *    `deleteAccount()` and then `auth.signOut()` + `router.replace('/')` on
 *    success.
 *
 * The 30s polling uses `useTimeoutPoll` (the vueuse 13.x replacement for
 * `useTimeoutFn`, which was removed in 13.0.0). The pattern is the same:
 * `resume()` while pending/failed, `pause()` once `synced`.
 */
export const useSettingsStore = defineStore('settings', () => {
  const api = useApi()
  const auth = useAuthStore()
  const router = useRouter()

  // --- state ---
  const profile = ref<User | null>(null)
  const company = ref<Company | null>(null)
  const availableCompanies = ref<Company[]>([])
  const activeCompanyId = ref<string | null>(localStorage.getItem('trama_active_company_id'))
  const syncStatus = ref<UserSyncStatus>('synced')

  const loadingProfile = ref(false)
  const loadingCompany = ref(false)
  const savingProfile = ref(false)
  const savingCompany = ref(false)
  const uploadingLogo = ref(false)

  // --- refreshCompany caching (Fase 36 retro, Jul 2026) ---
  //
  // Background: `refreshCompany()` was previously awaited on every
  // navigation by `router.beforeEach`, which meant a click on any
  // sidebar item, document tab, or invoices-table row triggered two
  // sequential HTTP calls (GET /companies + GET /companies/current)
  // before the route resolved. With a TLS-roundtrip to Dokploy that
  // costs ~500ms per call → a permanent 1s click-to-paint delay
  // across the whole app.
  //
  // Fix: a TTL cache + in-flight dedup on top of the existing fetch.
  // Default is `force: true` (preserved semantics — every mutation
  // site that already calls `refreshCompany()` keeps its fresh-fetch
  // guarantee). The router guard is the only call site that benefits
  // from caching and opts in via `refreshCompany({ force: false })`.
  const COMPANY_TTL_MS = 60_000
  const lastCompanyRefreshAt = ref(0)
  let companyRefreshInFlight: Promise<void> | null = null

  // --- Company-scope-change registry (Fase 36 retro, Jul 2026) ---
  //
  // Background: per-company Pinia stores (`documents`, `dianImporter`,
  // `retention-history`, `cost-center-allocations`, `invoices`) cache
  // tenant-scoped lists in memory. `setActiveCompany()` only mutated
  // the ref + `localStorage` — no other store subscribed to the
  // change. Switching the active company via the header `CompanySelector`
  // left stale rows from the previous tenant on screen until the user
  // hard-refreshed or navigated away.
  //
  // Fix: a tiny callback registry. Each per-company store registers
  // its own "reset + refetch" callback during its `defineStore` setup
  // via `onCompanyScopeChanged(cb)`. `setActiveCompany()` invokes all
  // subscribers on `nextTick()` after the ref + `localStorage` write
  // so the callbacks observe the new value synchronously.
  //
  // Same-id re-selections are a no-op (no callbacks fired) — see the
  // `setActiveCompany()` change in this same file.
  type ScopeChangeCallback = () => void
  const scopeSubscribers = new Set<ScopeChangeCallback>()
  function onCompanyScopeChanged(cb: ScopeChangeCallback): () => void {
    if (scopeSubscribers.has(cb)) return () => undefined
    scopeSubscribers.add(cb)
    return () => {
      scopeSubscribers.delete(cb)
    }
  }

  // --- getters ---
  const isAdmin = computed(() => {
    const r = profile.value?.role
    return r === 'admin' || r === 'super_admin'
  })

  // --- actions ---

  async function refreshProfile(): Promise<void> {
    loadingProfile.value = true
    try {
      const result = await api.get<User>('/users/me')
      profile.value = result
      // The BE always returns syncStatus on the User row, but be defensive
      // in case an older BE returns a row without the field.
      syncStatus.value = (result.syncStatus ?? 'synced') as UserSyncStatus
    } catch (e) {
      // Silent: the FE shouldn't block the Settings page on a 401/refresh race.
      // The auth store's 401 interceptor will redirect on the next request.
      // eslint-disable-next-line no-console
      console.warn('[settings] refreshProfile failed', e)
    } finally {
      loadingProfile.value = false
    }
  }

  async function refreshCompany(opts: { force?: boolean } = {}): Promise<void> {
    const force = opts.force ?? true

    // Cache hit (TTL window): membership + active company cannot have
    // changed during the last 60s in any reasonable flow. The router
    // guard awaits this in beforeEach — a skip here turns a 1s wait
    // into a microsecond, which is what removes the click delay.
    if (!force) {
      if (Date.now() - lastCompanyRefreshAt.value < COMPANY_TTL_MS) return
      // Concurrent calls within the TTL window share the same in-flight
      // promise — prevents the double-click case from doubling the
      // network traffic on a slow first-fetch.
      if (companyRefreshInFlight) return companyRefreshInFlight
    }

    const run = async (): Promise<void> => {
      loadingCompany.value = true
      try {
        // Refresh the list of companies the user has access to
        let companies: Company[] = []
        try {
          companies = await api.get<Company[]>('/companies')
        } catch (e) {
          // Fallback for older backends that don't have the /companies endpoint yet
          console.warn('[settings] /companies failed, falling back to /companies/current', e)
          const currentCompany = await api.get<Company>('/companies/current')
          companies = [currentCompany]
        }

        availableCompanies.value = companies

        if (companies.length > 0) {
          // Auto-select first company if none is selected or selected one is invalid
          if (!activeCompanyId.value || !companies.some(c => c.id === activeCompanyId.value)) {
            setActiveCompany(companies[0].id)
          }
        }

        // Refresh the current active company details
        if (activeCompanyId.value) {
          // If we only have one company and we just fetched it in the fallback, we could reuse it,
          // but fetching again ensures consistency.
          const result = await api.get<Company>(`/companies/current`)
          company.value = result
        }

        lastCompanyRefreshAt.value = Date.now()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[settings] refreshCompany failed completely', e)
      } finally {
        loadingCompany.value = false
        companyRefreshInFlight = null
      }
    }

    companyRefreshInFlight = run()
    return companyRefreshInFlight
  }

  function setActiveCompany(companyId: string) {
    // No-op when the new id equals the current one. Prevents spurious
    // refetches in the per-company stores when a user re-selects
    // the currently active company from the header dropdown.
    // See `onCompanyScopeChanged()` for the callback registry that
    // depends on this no-op behavior.
    if (companyId === activeCompanyId.value) return
    activeCompanyId.value = companyId
    localStorage.setItem('trama_active_company_id', companyId)
    // Fire all registered scope-change callbacks on the next tick so
    // subscribers observe the new `activeCompanyId.value` synchronously
    // (the ref write is reactive but `nextTick` keeps the ordering
    // predictable — subscribers that internally `await` something can
    // safely read `settings.activeCompanyId`).
    void nextTick(() => {
      // Snapshot the set so a callback that unregisters itself
      // mid-iteration doesn't perturb the loop.
      const subs = Array.from(scopeSubscribers)
      for (const cb of subs) {
        try {
          cb()
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[settings] onCompanyScopeChanged callback threw:', err)
        }
      }
    })
  }

  async function createCompany(dto: { nit: string; legalName: string }): Promise<Company> {
    savingCompany.value = true
    try {
      const newCompany = await api.post<Company>('/companies', dto)
      await refreshCompany()
      setActiveCompany(newCompany.id)
      await refreshCompany() // Reload current after switching
      return newCompany
    } finally {
      savingCompany.value = false
    }
  }

  /**
   * T-01 mass-assignment defense: the DTO is typed as a literal Pick
   * with only name + email — role, companyId, isActive, etc. cannot leak
   * through even if `profile.value` has them.
   */
  async function updateProfile(dto: { name?: string; email?: string }): Promise<User> {
    savingProfile.value = true
    try {
      const updated = await api.patch<User>('/users/me', dto)
      profile.value = updated
      // The BE sets syncStatus='pending' on PATCH; mirror that locally
      // so the badge appears immediately, then the next 30s poll will
      // confirm whether the Windmill callback has resolved it.
      syncStatus.value = (updated.syncStatus ?? 'pending') as UserSyncStatus
      return updated
    } finally {
      savingProfile.value = false
    }
  }

  async function updateCompany(
    dto: Partial<
      Pick<
        Company,
        | 'name'
        | 'address'
        | 'phone'
        | 'email'
        | 'timezone'
        | 'nit'
        | 'dv'
        | 'responsabilidadTributaria'
        | 'tipoContribuyente'
        | 'agenteRetefuente'
        | 'agenteReteiva'
        | 'agenteReteica'
        | 'city'
        | 'complementaryAddress'
      >
    >,
  ): Promise<Company> {
    savingCompany.value = true
    try {
      const updated = await api.patch<Company>('/companies/current', dto)
      company.value = updated
      return updated
    } finally {
      savingCompany.value = false
    }
  }

  /**
   * Upload the company logo. Backend returns `{ logoUrl: <signed-url> }`;
   * the BE persists the key in `companies.logoUrl` and we re-fetch the
   * company row to update the local `company.value` with the fresh signed URL.
   */
  async function uploadLogo(file: File): Promise<string> {
    uploadingLogo.value = true
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.upload<{ logoUrl: string }>('/companies/current/logo', fd)
      // Re-fetch so the local `company.logoUrl` reflects the fresh signed URL.
      await refreshCompany()
      return res.logoUrl
    } finally {
      uploadingLogo.value = false
    }
  }

  const uploadingRut = ref(false)

  async function uploadRut(file: File): Promise<string> {
    uploadingRut.value = true
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.upload<{ rutUrl: string }>('/companies/current/rut', fd)
      // Re-fetch so the local `company.rutFileUrl` reflects the fresh signed URL.
      await refreshCompany()
      return res.rutUrl
    } finally {
      uploadingRut.value = false
    }
  }

  async function changePassword(dto: {
    oldPassword: string
    newPassword: string
    newPasswordConfirm: string
  }): Promise<void> {
    await api.patch('/users/me/password', dto)
  }

  async function deleteAccount(): Promise<void> {
    await api.delete('/users/me')
    // Caller is responsible for signOut + redirect. We don't navigate here
    // because DeleteAccountDialog wants to surface a Spanish error toast
    // on 409 before any navigation happens.
  }

  // --- 30s sync polling (Plan 01.3 Task 1) ---

  /**
   * useTimeoutPoll re-arms itself after each `fn()` while `isActive` is true.
   * The `immediate: false` flag defers the first tick until `resume()` is
   * called explicitly by the `watch(syncStatus, ...)` below.
   */
  const { resume, pause } = useTimeoutPoll(
    async () => {
      // Fetch the latest profile. If sync is now 'synced', the watcher
      // (set up below) will call `pause()` automatically.
      await refreshProfile()
    },
    30_000,
    { immediate: false, immediateCallback: false },
  )

  // React to syncStatus changes — the most common trigger is the watcher
  // inside the component calling `updateProfile()` (which sets syncStatus
  // to 'pending' optimistically).
  watch(syncStatus, (s) => {
    if (s === 'pending' || s === 'failed') {
      resume()
    } else {
      pause()
    }
  })

  return {
    // state
    profile,
    company,
    availableCompanies,
    activeCompanyId,
    syncStatus,
    loadingProfile,
    loadingCompany,
    savingProfile,
    savingCompany,
    uploadingLogo,
    uploadingRut,
    // getters
    isAdmin,
    // actions
    refreshProfile,
    refreshCompany,
    setActiveCompany,
    onCompanyScopeChanged,
    createCompany,
    updateProfile,
    updateCompany,
    uploadLogo,
    uploadRut,
    changePassword,
    deleteAccount,
  }
})
